/**
 * PolicyOverridesTab — tenant-specific overrides (disable / force_allow / stricter).
 *
 * Operator can create a new override via a SideSheet form and revoke an
 * existing one with inline confirmation. Reason is required (ECTP Rule 7).
 */
import { zodResolver } from '@hookform/resolvers/zod';
import { Plus, Trash } from '@phosphor-icons/react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../shared/ui/Button';
import { Card, CardBody, CardHeader } from '../../../shared/ui/Card';
import { FieldError } from '../../../shared/ui/FieldError';
import { Input } from '../../../shared/ui/Input';
import { Label } from '../../../shared/ui/Label';
import { Select } from '../../../shared/ui/Select';
import { SideSheet } from '../../../shared/ui/SideSheet';
import { StatusBadge } from '../../../shared/ui/StatusBadge';
import { Textarea } from '../../../shared/ui/Textarea';
import {
  useCreatePolicyOverride,
  usePolicyOverrides,
  useRevokePolicyOverride,
} from '../../../features/business-policies/useBusinessPolicies';
import {
  POLICY_OVERRIDE_MODES,
  policyOverrideFormSchema,
  type PolicyOverrideFormValues,
  type PolicyTenantOverride,
} from '../../../features/business-policies/types';

const OVERRIDE_VARIANT = {
  disable: 'warning',
  force_allow: 'danger',
  stricter: 'info',
} as const;

export function PolicyOverridesTab({
  policyId,
}: {
  policyId: string;
}): JSX.Element {
  const { t } = useTranslation('adminPolicies');
  const overridesQuery = usePolicyOverrides(policyId);
  const revokeMutation = useRevokePolicyOverride(policyId);
  const [sheetOpen, setSheetOpen] = useState(false);

  const handleRevoke = async (o: PolicyTenantOverride) => {
    const confirmed = window.confirm(
      t('overrides.confirmRevoke', { tenant: o.tenantName }),
    );
    if (!confirmed) return;
    await revokeMutation.mutateAsync(o.id);
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div>
            <h3 className="biz-heading font-semibold">{t('overrides.title')}</h3>
            <p className="text-biz-sm text-biz-muted-foreground">
              {t('overrides.subtitle')}
            </p>
          </div>
          <Button
            intent="primary"
            leftIcon={<Plus size={18} weight="regular" />}
            onClick={() => setSheetOpen(true)}
          >
            {t('overrides.add')}
          </Button>
        </CardHeader>
        {overridesQuery.isLoading ? (
          <CardBody aria-busy="true">
            <div className="h-4 w-1/3 bg-biz-muted rounded animate-pulse" />
          </CardBody>
        ) : (overridesQuery.data?.length ?? 0) === 0 ? (
          <CardBody className="text-biz-muted-foreground text-center">
            {t('overrides.empty')}
          </CardBody>
        ) : (
          <ul className="divide-y divide-biz-border">
            {overridesQuery.data!.map((o) => (
              <li
                key={o.id}
                className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-biz-content-lg px-biz-card py-biz-content-lg"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-biz-content flex-wrap">
                    <span className="font-semibold truncate">{o.tenantName}</span>
                    <StatusBadge
                      variant={OVERRIDE_VARIANT[o.override]}
                      label={t(`overrides.modes.${o.override}` as const)}
                    />
                  </div>
                  <p className="mt-biz-content text-biz-sm text-biz-foreground italic">
                    "{o.reason}"
                  </p>
                  <p className="mt-1 text-biz-xs text-biz-muted-foreground">
                    {t('overrides.createdBy', {
                      who: o.createdBy,
                      when: new Date(o.createdAt).toLocaleDateString(),
                    })}
                    {o.expiresAt
                      ? ` · ${t('overrides.expires', {
                          when: new Date(o.expiresAt).toLocaleDateString(),
                        })}`
                      : ''}
                  </p>
                </div>
                <Button
                  intent="ghost"
                  size="sm"
                  leftIcon={<Trash size={16} weight="regular" />}
                  onClick={() => handleRevoke(o)}
                  loading={revokeMutation.isPending}
                  className="md:self-center"
                >
                  {t('overrides.revoke')}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <CreateOverrideSheet
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        policyId={policyId}
      />
    </>
  );
}

function CreateOverrideSheet({
  open,
  onOpenChange,
  policyId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  policyId: string;
}): JSX.Element {
  const { t } = useTranslation('adminPolicies');
  const createMutation = useCreatePolicyOverride(policyId);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<PolicyOverrideFormValues>({
    resolver: zodResolver(policyOverrideFormSchema),
    defaultValues: {
      tenantId: '',
      override: 'disable',
      reason: '',
      expiresAt: '',
    },
  });

  const onSubmit = async (values: PolicyOverrideFormValues) => {
    await createMutation.mutateAsync({
      ...values,
      expiresAt: values.expiresAt || undefined,
    });
    reset();
    onOpenChange(false);
  };

  const errorFor = (key: keyof PolicyOverrideFormValues): string | undefined => {
    const e = errors[key];
    if (!e) return undefined;
    if (e.message === 'tenantRequired') return t('overrides.tenantRequired');
    return t('overrides.fieldRequired');
  };

  return (
    <SideSheet
      open={open}
      onOpenChange={onOpenChange}
      title={t('overrides.sheetTitle')}
      description={t('overrides.sheetSubtitle')}
      closeLabel={t('overrides.cancel')}
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-biz-content-lg" noValidate>
        <div>
          <Label htmlFor="ov-tenant">{t('overrides.tenantLabel')}</Label>
          <Input
            id="ov-tenant"
            placeholder={t('overrides.tenantPlaceholder')}
            invalid={Boolean(errors.tenantId)}
            {...register('tenantId')}
          />
          <FieldError id="ov-tenant-error">{errorFor('tenantId')}</FieldError>
        </div>
        <div>
          <Label htmlFor="ov-mode">{t('overrides.modeLabel')}</Label>
          <Select id="ov-mode" invalid={Boolean(errors.override)} {...register('override')}>
            {POLICY_OVERRIDE_MODES.map((m) => (
              <option key={m} value={m}>
                {t(`overrides.modes.${m}` as const)}
              </option>
            ))}
          </Select>
        </div>
        <div>
          <Label htmlFor="ov-expires">{t('overrides.expiresLabel')}</Label>
          <Input
            id="ov-expires"
            type="date"
            {...register('expiresAt')}
          />
          <p className="mt-biz-content text-biz-xs text-biz-muted-foreground">
            {t('overrides.expiresHint')}
          </p>
        </div>
        <div>
          <Label htmlFor="ov-reason">{t('overrides.reasonLabel')}</Label>
          <Textarea
            id="ov-reason"
            rows={4}
            placeholder={t('overrides.reasonPlaceholder')}
            invalid={Boolean(errors.reason)}
            {...register('reason')}
          />
          <FieldError id="ov-reason-error">{errorFor('reason')}</FieldError>
          <p className="mt-biz-content text-biz-xs text-biz-muted-foreground">
            {t('overrides.reasonHint')}
          </p>
        </div>
        <div className="flex items-center justify-end gap-biz-content-lg pt-biz-content-lg border-t border-biz-border">
          <Button type="button" intent="secondary" onClick={() => onOpenChange(false)}>
            {t('overrides.cancel')}
          </Button>
          <Button
            type="submit"
            intent="primary"
            leftIcon={<Plus size={18} weight="regular" />}
            loading={isSubmitting || createMutation.isPending}
          >
            {t('overrides.submit')}
          </Button>
        </div>
      </form>
    </SideSheet>
  );
}
